import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import AskIcon from 'shared/components/ui/AskIcon';
import { format } from 'date-fns';
import DrawerScrim, { DRAWER_OVERLAY_CLASS } from 'shared/components/ui/DrawerScrim';
import MetaGrid from 'shared/components/ui/MetaGrid';
import TrodoLoader from 'shared/components/ui/TrodoLoader';
import EventsTimeFilter from 'features/activity/components/EventsTimeFilter';
import AgentRunDetailPage from 'features/agent-analytics/pages/AgentRunDetailPage';
import ConversationDetailDrawer from 'features/agent-analytics/components/ConversationDetailDrawer';
import {
  CopyButton, FeedbackCell, StatusPill, ThumbDownIcon, ThumbUpIcon,
  formatCost, formatDuration, formatNumber, formatRelativeTime, truncate,
} from 'features/agent-analytics/components/runCells';
import { presetToISORange, DEFAULT_LIST_PRESET } from 'shared/utils/timePresets';
import { openAsk } from 'shared/askDraft';
import { getUserProfile } from 'features/users/userProfileApi';
import { Card, Donut, NAMED_PALETTE } from 'features/agent-analytics/components/failures/FailuresSummary';

/**
 * An end user, seen through their agent runs. Opened in place from anywhere a
 * distinct_id shows (see openUserProfile); runs and conversations stack on top
 * of it, the same way they do over the conversation drawer. Everything below
 * the header is for the chosen period (last 30 days to start).
 */
export default function UserProfileDrawer({ distinctId, teamId, teamSlug, onClose }) {
  const location = useLocation();
  const [preset, setPreset] = useState(DEFAULT_LIST_PRESET);
  const [range, setRange] = useState(() => presetToISORange(DEFAULT_LIST_PRESET));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('overview');
  const [openRunId, setOpenRunId] = useState(null);
  const [openConversation, setOpenConversation] = useState(null);

  // A different user starts again on the default period and the overview.
  useEffect(() => {
    setPreset(DEFAULT_LIST_PRESET);
    setRange(presetToISORange(DEFAULT_LIST_PRESET));
    setData(null); setTab('overview'); setOpenRunId(null); setOpenConversation(null);
  }, [distinctId]);

  useEffect(() => {
    if (!distinctId || !teamId || !range) return undefined;
    let live = true;
    setLoading(true); setError(null);
    getUserProfile({ teamId, distinctId, from: range.from, to: range.to })
      .then((d) => { if (live) setData(d); })
      .catch((e) => { if (live) setError(e?.response?.data?.error || 'Could not load this user'); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [distinctId, teamId, range]);

  // Esc closes the top-most layer only: a stacked run or conversation first.
  // Read in the CAPTURE phase, before any drawer handles the key: the run
  // drawer closes itself on the same press, React re-renders synchronously,
  // and a bubble-phase listener re-registered by that render would see nothing
  // stacked and close this drawer too.
  const stackedRef = useRef(false);
  stackedRef.current = Boolean(openRunId || openConversation);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape' || stackedRef.current) return;
      // Esc inside a field or the period picker is theirs, not the drawer's.
      if (e.target?.closest?.('input, textarea, select, [role="dialog"], [role="menu"], [role="listbox"]')) return;
      // This drawer is the top layer: close it and keep the key from also
      // closing a drawer of the page underneath.
      e.stopPropagation();
      onClose?.();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  const onPeriod = (next) => {
    const p = next?.preset || (next?.type === 'custom' ? 'custom' : '');
    setPreset(p || DEFAULT_LIST_PRESET);
    setRange(next?.from || next?.to
      ? { from: next.from || range.from, to: next.to || new Date().toISOString() }
      : presetToISORange(p) || range);
  };

  const t = data?.totals;
  const props = data?.identity?.custom_properties || {};
  // A name only when it adds something: many teams use the email as the id.
  const nameRaw = props.name || props.full_name || props.email || null;
  const name = nameRaw && String(nameRaw) !== distinctId ? String(nameRaw) : null;
  const periodLabel = range ? `${fmtDay(range.from)} – ${fmtDay(range.to)}` : '';

  const metaCells = useMemo(() => {
    if (!t) return [];
    const errorPct = t.runs ? Math.round((t.errors / t.runs) * 100) : 0;
    const ev = data.evals;
    const fb = data.feedback;
    return [
      name && { label: 'Name', title: name, content: <span>{name}</span> },
      { label: 'Last active', title: fmtDateTime(t.last_run_at), content: <span className="tabular-nums">{t.last_run_at ? formatRelativeTime(t.last_run_at) : '—'}</span> },
      { label: 'First seen', title: fmtDateTime(t.first_run_at), content: <span className="tabular-nums">{t.first_run_at ? fmtDay(t.first_run_at) : '—'}</span> },
      { label: 'Runs', content: <span className="tabular-nums">{formatNumber(t.runs)}</span> },
      { label: 'Conversations', content: <span className="tabular-nums">{formatNumber(t.conversations)}</span> },
      {
        label: 'Errors',
        content: (
          <span className={`tabular-nums ${t.errors ? 'text-[color:var(--feedback-error)]' : ''}`}>
            {formatNumber(t.errors)}{t.runs ? ` (${errorPct}%)` : ''}
          </span>
        ),
      },
      ev.scored ? {
        label: 'Evals passed',
        title: `${formatNumber(ev.passed)} of ${formatNumber(ev.scored)} results`,
        content: <span className="tabular-nums">{pct(ev.passed, ev.scored)}% <span className="text-[color:var(--text-muted)]">of {formatNumber(ev.scored)}</span></span>,
      } : null,
      fb.count ? {
        label: 'Feedback',
        content: (
          <span className="inline-flex items-center gap-2 tabular-nums">
            <span className="inline-flex items-center gap-1"><ThumbUpIcon />{fb.positive}</span>
            <span className="inline-flex items-center gap-1"><ThumbDownIcon />{fb.negative}</span>
            {fb.avg_rating != null ? <span className="text-[color:var(--text-muted)]">{fb.avg_rating.toFixed(1)} avg</span> : null}
          </span>
        ),
      } : null,
      { label: 'Cost', content: <span className="tabular-nums">{formatCost(t.cost)}</span> },
      { label: 'Tokens', title: `${formatNumber(t.tokens_in)} in / ${formatNumber(t.tokens_out)} out`, content: <span className="tabular-nums">{compact(t.tokens_in)} / {compact(t.tokens_out)}</span> },
    ].filter(Boolean);
  }, [t, data, name]);

  if (!distinctId) return null;

  const evalHref = (lineageId) => `/${encodeURIComponent(teamSlug || '')}/agent-analytics/evals?eval=${encodeURIComponent(lineageId)}&filter=failed`;
  // Lucid is the page itself on /chat; the side panel is for every other page.
  const onChatPage = /\/chat\/?$/.test(location.pathname);
  const analyze = () => openAsk(`About the end user "${distinctId}", ${periodLabel}: `);

  return (
    <>
      <div className={DRAWER_OVERLAY_CLASS} role="region" aria-label="User profile">
        <DrawerScrim onClose={onClose} />
        <section
          className="pointer-events-auto relative flex h-full w-[min(100%,880px)] flex-col overflow-hidden border-l border-t border-[color:var(--border-default)] bg-surface shadow-2xl sm:w-[min(92%,880px)]"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          <div className="flex min-h-0 flex-1 flex-col gap-4 px-4 pt-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="min-w-0 max-w-full truncate font-mono text-[13px] font-medium text-[color:var(--text-primary)]" title={distinctId}>
                {distinctId}
              </span>
              <CopyButton value={distinctId} label="Copy user id" />
              <div className="ml-auto flex items-center gap-2">
                <EventsTimeFilter selectedPreset={preset} onTimeFilterChange={onPeriod} />
                {!onChatPage ? (
                  <button
                    type="button"
                    onClick={analyze}
                    className="inline-flex h-8 items-center gap-1.5 rounded-md border border-[color:var(--border-default)] bg-surface px-2.5 text-[12px] font-medium text-[color:var(--text-primary)] hover:bg-surface-hover"
                  >
                    <AskIcon size={14} aria-hidden />
                    Analyze with Lucid
                  </button>
                ) : null}
                <button type="button" onClick={onClose} aria-label="Close" className="flex h-8 w-8 flex-none items-center justify-center rounded-md text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]">
                  <X size={16} />
                </button>
              </div>
            </div>

            {error ? (
              <div className="py-12 text-center text-[12px] text-[color:var(--feedback-error)]">{error}</div>
            ) : !data ? (
              <div className="flex justify-center py-12"><TrodoLoader size={64} label="Loading user" /></div>
            ) : (
              <>
                <div className={loading ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
                  <MetaGrid cells={metaCells} />
                </div>
                <div className="-mx-4 flex min-h-0 flex-1 flex-col overflow-hidden border-t border-[color:var(--border-default)] bg-surface">
                  <div className="flex flex-none items-center gap-1 overflow-x-auto border-b border-[color:var(--border-default)] bg-surface px-3">
                    <Tab id="overview" label="Overview" tab={tab} onClick={setTab} />
                    <Tab id="runs" label="Runs" tab={tab} onClick={setTab} badge={t.runs || null} />
                    <Tab id="conversations" label="Conversations" tab={tab} onClick={setTab} badge={t.conversations || null} />
                    <Tab id="evals" label="Evals" tab={tab} onClick={setTab} badge={data.evals.by_eval.length || null} />
                    <Tab id="feedback" label="Feedback" tab={tab} onClick={setTab} badge={data.feedback.count || null} />
                    <Tab id="properties" label="Properties" tab={tab} onClick={setTab} badge={Object.keys(props).length || null} />
                  </div>
                  <div className={`min-h-0 flex-1 overflow-y-auto p-3 ${loading ? 'opacity-60 transition-opacity' : 'transition-opacity'}`}>
                    {tab === 'overview' ? (
                      <Overview data={data} onOpenRun={setOpenRunId} onTab={setTab} />
                    ) : tab === 'runs' ? (
                      <RunsTable runs={data.runs} total={t.runs} onOpen={setOpenRunId} />
                    ) : tab === 'conversations' ? (
                      <ConversationsTable rows={data.conversations} total={t.conversations} onOpen={setOpenConversation} />
                    ) : tab === 'evals' ? (
                      <EvalsTable rows={data.evals.by_eval} evalHref={evalHref} scope={data.scope} totalRuns={t.runs} />
                    ) : tab === 'feedback' ? (
                      <FeedbackList rows={data.feedback.recent} onOpenRun={setOpenRunId} scope={data.scope} totalRuns={t.runs} />
                    ) : (
                      <PropertiesTable props={props} updatedAt={data.identity?.custom_properties_last_updated} />
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </section>
      </div>

      {openConversation ? (
        <ConversationDetailDrawer
          conversationId={openConversation.conversation_id}
          teamId={teamId}
          onClose={() => setOpenConversation(null)}
        />
      ) : null}
      {openRunId ? (
        <AgentRunDetailPage runId={openRunId} teamId={teamId} onClose={() => setOpenRunId(null)} />
      ) : null}
    </>
  );
}

const pct = (a, b) => (b ? Math.round((a / b) * 100) : 0);

/** 1 Oct 2026 — dates in the reader's locale order, no seconds. */
function fmtDay(iso) {
  if (!iso) return '—';
  try { return format(new Date(iso), 'd MMM yyyy'); } catch { return String(iso); }
}

/** 1 Oct 2026, 14:05 — the exact moment, for tooltips. */
function fmtDateTime(iso) {
  if (!iso) return '';
  try { return format(new Date(iso), 'd MMM yyyy, HH:mm'); } catch { return String(iso); }
}

/** 134851942 → 134.9M: the header cell holds a glance, the tooltip the exact count. */
function compact(n) {
  const v = Number(n) || 0;
  if (v >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e4) return `${(v / 1e3).toFixed(1)}K`;
  return formatNumber(v);
}

function Tab({ id, label, tab, badge, onClick }) {
  const active = tab === id;
  return (
    <button
      type="button"
      onClick={() => onClick(id)}
      aria-current={active ? 'page' : undefined}
      className={`relative flex-none px-3 py-2 text-[12px] font-medium transition-colors ${active ? 'text-[color:var(--text-primary)]' : 'text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]'}`}
    >
      <span className="inline-flex items-center gap-1.5">
        {label}
        {badge != null ? (
          <span className={`rounded-full px-1.5 text-[10px] font-semibold ${active ? 'bg-surface-hover text-[color:var(--text-primary)]' : 'bg-[color:var(--border-subtle)] text-[color:var(--text-secondary)]'}`}>
            {formatNumber(badge)}
          </span>
        ) : null}
      </span>
      <span className={`absolute inset-x-2 bottom-0 h-[2px] rounded-t ${active ? 'bg-[color:var(--text-primary)]' : 'bg-transparent'}`} />
    </button>
  );
}

const TH = 'whitespace-nowrap px-2 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-muted)]';
const TD = 'px-2 py-1.5 align-top';
const ROW_CLICK = 'cursor-pointer border-t border-[color:var(--border-subtle)] hover:bg-surface-hover';
const ROW = 'border-t border-[color:var(--border-subtle)]';

/** Tables scroll sideways on a narrow screen instead of squeezing their columns. */
function Table({ children, minWidth = 560 }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[12px]" style={{ minWidth }}>{children}</table>
    </div>
  );
}

function Empty({ children }) {
  return <div className="py-8 text-center text-[12px] text-[color:var(--text-muted)]">{children}</div>;
}

/** Scope note shown only when the user has more runs in the period than evals and feedback cover. */
function ScopeNote({ scope, totalRuns }) {
  if (!scope || totalRuns <= scope.recent_runs) return null;
  return <div className="pt-2 text-[11px] text-[color:var(--text-muted)]">From the latest {formatNumber(scope.recent_runs)} of {formatNumber(totalRuns)} runs</div>;
}

function Overview({ data, onOpenRun, onTab }) {
  const comments = data.feedback.recent.filter((f) => f.comment).slice(0, 3);
  if (!data.totals.runs) return <Empty>No runs in this period</Empty>;
  const ev = data.evals;
  // Pass / fail is each eval's own verdict, so green and red carry a defined
  // meaning here. Which evals fail is just a breakdown: distinct hues, no
  // judgement, the same donuts the Issues page uses.
  const outcome = [
    { key: 'passed', label: 'Passed', count: ev.passed, color: 'var(--feedback-success)' },
    { key: 'failed', label: 'Failed', count: ev.failed, color: 'var(--feedback-error)' },
  ].filter((c) => c.count > 0);
  const failing = ev.by_eval.filter((e) => e.failed > 0).sort((a, b) => b.failed - a.failed);
  const top = failing.slice(0, 6).map((e, i) => ({ key: e.lineage_id, label: e.name, count: e.failed, color: NAMED_PALETTE[i % NAMED_PALETTE.length] }));
  const rest = failing.slice(6).reduce((n, e) => n + e.failed, 0);
  const byEval = rest ? [...top, { key: 'other', label: 'Other evals', count: rest, color: 'color-mix(in srgb, var(--text-muted) 60%, transparent)', other: true }] : top;
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <Card title={`Runs per ${data.series.bucket}`} action={<span className="ml-auto text-[11px] text-[color:var(--text-muted)]"><Legend /></span>} className="md:col-span-2">
        <SeriesBars series={data.series} />
      </Card>

      {ev.scored ? (
        <>
          <Card title="Eval results" sub={`${formatNumber(ev.by_eval.length)} evals`}>
            <Donut items={outcome} total={ev.scored} />
          </Card>
          <Card
            title="Failures by eval"
            action={<button type="button" onClick={() => onTab('evals')} className="ml-auto text-[11px] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]">All evals</button>}
          >
            {byEval.length ? <Donut items={byEval} total={ev.failed} /> : (
              <div className="flex h-[132px] items-center justify-center text-[12px] text-[color:var(--text-muted)]">No failures</div>
            )}
          </Card>
        </>
      ) : null}

      <Card title="Agents" className="md:col-span-2">
        <Table minWidth={420}>
          <thead><tr><th className={TH}>Agent</th><th className={`${TH} text-right`}>Runs</th><th className={`${TH} text-right`}>Errors</th><th className={`${TH} text-right`}>Cost</th><th className={`${TH} text-right`}>Last run</th></tr></thead>
          <tbody>
            {data.agents.map((a) => (
              <tr key={a.agent_name} className={ROW}>
                <td className={TD}>{a.agent_name}</td>
                <td className={`${TD} text-right tabular-nums`}>{formatNumber(a.runs)}</td>
                <td className={`${TD} text-right tabular-nums ${a.errors ? 'text-[color:var(--feedback-error)]' : 'text-[color:var(--text-muted)]'}`}>{formatNumber(a.errors)}</td>
                <td className={`${TD} text-right tabular-nums`}>{formatCost(a.cost)}</td>
                <td className={`${TD} whitespace-nowrap text-right tabular-nums text-[color:var(--text-muted)]`} title={fmtDateTime(a.last_at)}>{formatRelativeTime(a.last_at)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>

      {comments.length ? (
        <Card title="Latest feedback" className="md:col-span-2">
          <FeedbackList rows={comments} onOpenRun={onOpenRun} />
        </Card>
      ) : null}
    </div>
  );
}

function Legend() {
  return (
    <span className="inline-flex items-center gap-3">
      <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-[color:var(--trodo-primary)] opacity-70" />Runs</span>
      <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-[color:var(--feedback-error)]" />Errors</span>
    </span>
  );
}

/** Runs per day (or week, past ~3 months), errors stacked at the base. Every bucket in the period, zeros included. */
function SeriesBars({ series }) {
  const step = series.bucket === 'week' ? 7 : 1;
  const byDay = new Map(series.rows.map((r) => [r.day, r]));
  const buckets = [];
  const start = new Date(series.from);
  // Align to the bucket the server truncates to: UTC midnight, Monday for weeks.
  let d = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate()));
  if (step === 7) d = new Date(d.getTime() - ((d.getUTCDay() + 6) % 7) * 86400000);
  const end = new Date(series.to).getTime();
  while (d.getTime() <= end && buckets.length < 400) {
    const key = d.toISOString().slice(0, 10);
    buckets.push({ key, runs: byDay.get(key)?.runs || 0, errors: byDay.get(key)?.errors || 0 });
    d = new Date(d.getTime() + step * 86400000);
  }
  const max = Math.max(1, ...buckets.map((b) => b.runs));
  const H = 72;
  return (
    <div>
      <div className="flex items-end gap-[2px]" style={{ height: H }}>
        {buckets.map((b) => {
          const h = Math.round((b.runs / max) * H);
          const eh = b.runs ? Math.max(b.errors ? 2 : 0, Math.round((b.errors / b.runs) * h)) : 0;
          return (
            <div
              key={b.key}
              className="flex min-w-[2px] flex-1 flex-col justify-end"
              title={`${step === 7 ? 'Week of ' : ''}${fmtDay(b.key)}: ${formatNumber(b.runs)} runs${b.errors ? `, ${formatNumber(b.errors)} errors` : ''}`}
            >
              {h - eh > 0 ? <div style={{ height: h - eh }} className="rounded-t-sm bg-[color:var(--trodo-primary)] opacity-70" /> : null}
              {eh ? <div style={{ height: eh }} className={`${h - eh > 0 ? '' : 'rounded-t-sm'} bg-[color:var(--feedback-error)]`} /> : null}
            </div>
          );
        })}
      </div>
      <div className="mt-1.5 flex justify-between text-[10px] tabular-nums text-[color:var(--text-disabled)]">
        <span>{fmtDay(buckets[0]?.key)}</span><span>{fmtDay(buckets[buckets.length - 1]?.key)}</span>
      </div>
    </div>
  );
}

function EvalPills({ evals }) {
  if (!evals) return <span className="text-[color:var(--text-muted)]">—</span>;
  return (
    <span className="inline-flex gap-2 tabular-nums">
      {evals.passed ? <span className="text-[color:var(--feedback-success)]">{evals.passed} passed</span> : null}
      {evals.failed ? <span className="text-[color:var(--feedback-error)]">{evals.failed} failed</span> : null}
    </span>
  );
}

function RunsTable({ runs, total, onOpen }) {
  if (!runs.length) return <Empty>No runs in this period</Empty>;
  return (
    <>
      <Table minWidth={760}>
        <thead>
          <tr>
            <th className={TH}>When</th><th className={TH}>Agent</th><th className={TH}>Status</th><th className={TH}>Input</th>
            <th className={TH}>Evals</th><th className={TH}>Feedback</th><th className={`${TH} text-right`}>Duration</th><th className={`${TH} text-right`}>Cost</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((r) => (
            <tr key={r.run_id} className={ROW_CLICK} onClick={() => onOpen(r.run_id)}>
              <td className={`${TD} whitespace-nowrap tabular-nums text-[color:var(--text-muted)]`} title={fmtDateTime(r.started_at)}>{formatRelativeTime(r.started_at)}</td>
              <td className={`${TD} whitespace-nowrap`}>{r.agent_name}</td>
              <td className={TD}><StatusPill status={r.status} /></td>
              <td className={`${TD} max-w-[240px] truncate text-[color:var(--text-secondary)]`} title={r.input_preview || ''}>{truncate(r.input_preview || '', 60)}</td>
              <td className={`${TD} whitespace-nowrap`}><EvalPills evals={r.evals} /></td>
              <td className={TD}><FeedbackCell row={r} /></td>
              <td className={`${TD} text-right tabular-nums`}>{formatDuration(r.duration_ms)}</td>
              <td className={`${TD} text-right tabular-nums`}>{formatCost(r.total_cost)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
      {total > runs.length ? <div className="pt-2 text-[11px] text-[color:var(--text-muted)]">Latest {runs.length} of {formatNumber(total)}</div> : null}
    </>
  );
}

function ConversationsTable({ rows, total, onOpen }) {
  if (!rows.length) return <Empty>No conversations in this period</Empty>;
  return (
    <>
      <Table minWidth={520}>
        <thead>
          <tr><th className={TH}>Last activity</th><th className={TH}>Conversation</th><th className={TH}>Agents</th><th className={`${TH} text-right`}>Runs</th><th className={`${TH} text-right`}>Errors</th></tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.conversation_id} className={ROW_CLICK} onClick={() => onOpen(c)}>
              <td className={`${TD} whitespace-nowrap tabular-nums text-[color:var(--text-muted)]`} title={fmtDateTime(c.last_at)}>{formatRelativeTime(c.last_at)}</td>
              <td className={`${TD} font-mono text-[11px]`} title={c.conversation_id}>{truncate(c.conversation_id, 28)}</td>
              <td className={TD}>{(c.agents || []).join(', ')}</td>
              <td className={`${TD} text-right tabular-nums`}>{formatNumber(c.runs)}</td>
              <td className={`${TD} text-right tabular-nums ${c.errors ? 'text-[color:var(--feedback-error)]' : 'text-[color:var(--text-muted)]'}`}>{formatNumber(c.errors)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
      {total > rows.length ? <div className="pt-2 text-[11px] text-[color:var(--text-muted)]">Latest {rows.length} of {formatNumber(total)}</div> : null}
    </>
  );
}

function EvalsTable({ rows, evalHref, scope, totalRuns }) {
  if (!rows.length) return <Empty>No eval results in this period</Empty>;
  return (
    <>
      <Table minWidth={560}>
        <thead>
          <tr><th className={TH}>Eval</th><th className={TH}>On</th><th className={`${TH} text-right`}>Passed</th><th className={`${TH} text-right`}>Failed</th><th className={`${TH} text-right`}>Pass rate</th><th className={`${TH} text-right`}>Last failure</th></tr>
        </thead>
        <tbody>
          {rows.map((e) => (
            <tr key={e.lineage_id} className={ROW}>
              <td className={TD}><a href={evalHref(e.lineage_id)} className="text-[color:var(--text-primary)] underline-offset-2 hover:underline">{e.name}</a></td>
              <td className={`${TD} text-[color:var(--text-muted)]`}>{e.target_kind}</td>
              <td className={`${TD} text-right tabular-nums`}>{formatNumber(e.passed)}</td>
              <td className={`${TD} text-right tabular-nums ${e.failed ? 'text-[color:var(--feedback-error)]' : 'text-[color:var(--text-muted)]'}`}>{formatNumber(e.failed)}</td>
              <td className={`${TD} text-right tabular-nums`}>{pct(e.passed, e.scored)}%</td>
              <td className={`${TD} whitespace-nowrap text-right tabular-nums text-[color:var(--text-muted)]`} title={fmtDateTime(e.last_failed_at)}>{e.last_failed_at ? formatRelativeTime(e.last_failed_at) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </Table>
      <ScopeNote scope={scope} totalRuns={totalRuns} />
    </>
  );
}

function FeedbackList({ rows, onOpenRun, scope, totalRuns }) {
  if (!rows.length) return <Empty>No feedback in this period</Empty>;
  return (
    <>
      <Table minWidth={420}>
        <tbody>
          {rows.map((f, i) => (
            <tr key={`${f.run_id}-${i}`} className={ROW_CLICK} onClick={() => onOpenRun(f.run_id)}>
              <td className={`${TD} w-24 whitespace-nowrap tabular-nums text-[color:var(--text-muted)]`} title={fmtDateTime(f.timestamp)}>{formatRelativeTime(f.timestamp)}</td>
              <td className={`${TD} w-20`}><FeedbackCell row={{ feedback_satisfaction: f.satisfaction, feedback_rating: f.rating }} /></td>
              <td className={`${TD} text-[color:var(--text-primary)]`}>{f.comment || <span className="text-[color:var(--text-muted)]">—</span>}</td>
              <td className={`${TD} whitespace-nowrap text-right text-[color:var(--text-muted)]`}>{f.agent_name}</td>
            </tr>
          ))}
        </tbody>
      </Table>
      <ScopeNote scope={scope} totalRuns={totalRuns} />
    </>
  );
}

function PropertiesTable({ props, updatedAt }) {
  const keys = Object.keys(props).sort();
  if (!keys.length) return <Empty>No properties</Empty>;
  return (
    <>
      <Table minWidth={320}>
        <tbody>
          {keys.map((k) => {
            const v = props[k];
            const text = v != null && typeof v === 'object' ? JSON.stringify(v) : String(v ?? '');
            return (
              <tr key={k} className={ROW}>
                <td className={`${TD} w-48 font-mono text-[11px] text-[color:var(--text-muted)]`}>{k}</td>
                <td className={`${TD} break-all text-[color:var(--text-primary)]`}>{text}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
      {updatedAt ? <div className="pt-2 text-[11px] text-[color:var(--text-muted)]">Updated {fmtDay(updatedAt)}</div> : null}
    </>
  );
}
